import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building2, ChevronRight } from 'lucide-react';

const API   = process.env.REACT_APP_BACKEND_URL;
const BRAND = 'linear-gradient(135deg,#2cbdc4,#094f6f)';

export default function OnboardingLandlord() {
  const [step, setStep]       = useState(1);
  const [form, setForm]       = useState({ company_name: '', abn: '', portfolio_size: '', primary_suburb: '' });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const { token }  = useAuth();
  const navigate   = useNavigate();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.portfolio_size) { setError('Please select your portfolio size.'); return; }
    if (!form.primary_suburb) { setError('Please enter your primary suburb.'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/onboarding/landlord`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (res.ok) setStep(2);
      else setError('Something went wrong. Please try again.');
    } catch { setError('Network error. Please try again.'); }
    finally { setSaving(false); }
  };

  if (step === 1) return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg,#041e2b,#094f6f)' }}>
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
            style={{ background: BRAND }}>
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 px-4 py-1.5 rounded-full text-xs font-bold text-white/60 uppercase tracking-wide mb-3">
            Step 1 of 2 — Business Details
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Tell us about your portfolio</h1>
          <p className="text-white/50 text-sm">Helps us match you with the right renters</p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company or Trading Name <span className="text-gray-400 font-normal">(optional)</span></label>
              <input value={form.company_name} onChange={e => set('company_name', e.target.value)}
                placeholder="e.g. Smith Property Group"
                className="w-full h-11 border border-gray-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/25" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">ABN <span className="text-gray-400 font-normal">(optional)</span></label>
              <input value={form.abn} onChange={e => set('abn', e.target.value)}
                placeholder="e.g. 51 824 753 556"
                className="w-full h-11 border border-gray-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/25" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Property portfolio size *</label>
              <div className="grid grid-cols-3 gap-2">
                {['1–5', '6–20', '20+'].map(s => (
                  <button key={s} type="button" onClick={() => set('portfolio_size', s)}
                    className={`py-3 rounded-xl border-2 text-sm font-bold transition-all ${
                      form.portfolio_size === s
                        ? 'border-[#094f6f] text-[#094f6f] bg-[#094f6f]/5'
                        : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                    {s}<br /><span className="text-xs font-normal">properties</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Primary suburb of listings *</label>
              <input value={form.primary_suburb} onChange={e => set('primary_suburb', e.target.value)}
                placeholder="e.g. Parramatta NSW"
                className="w-full h-11 border border-gray-200 rounded-xl px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/25"
                required />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button type="submit" disabled={saving}
              className="w-full h-12 rounded-xl font-bold text-white text-sm disabled:opacity-50 hover:opacity-90 transition-opacity"
              style={{ background: BRAND }}>
              {saving ? 'Saving…' : 'Continue →'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg,#041e2b,#083a54)' }}>
      <div className="w-full max-w-md text-center">
        <div className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-3xl p-10">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: BRAND }}>
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-black text-white mb-3">Welcome to the Landlord Portal</h2>
          <p className="text-white/50 text-sm mb-6 leading-relaxed">
            Your property dashboard is ready. You can now create listings, view tenant leads, and manage enquiries — all in one place.
          </p>
          <div className="bg-white/5 border border-amber-400/30 rounded-2xl p-4 mb-8 text-left">
            <p className="text-amber-400 text-xs font-bold uppercase tracking-widest mb-1">Verification Required</p>
            <p className="text-white/60 text-xs leading-relaxed">
              Your listings will go live once your ID is verified. You have <strong className="text-white">7 days</strong> free access — verify early to unlock full visibility.
            </p>
          </div>
          <div className="space-y-3">
            <button onClick={() => navigate('/landlord-dashboard')}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-white text-sm hover:opacity-90 transition-opacity"
              style={{ background: BRAND }}>
              Go to my dashboard <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={() => navigate('/landlord-dashboard?tab=verify')}
              className="w-full py-3 rounded-2xl border border-white/20 text-white/60 text-sm font-medium hover:bg-white/5 transition-colors">
              Verify ID now — earn 500 pts
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
